import React, { useState, useEffect } from 'react';
import { formatIST } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { validateField, formatApiError } from '@/lib/validators';
import { Plus, RefreshCw, History, ChevronDown, ChevronUp } from 'lucide-react';
import BuyerPanel from './CreateOrder/BuyerPanel';
import ItemsGrid from './CreateOrder/ItemsGrid';
import OrderStrip from './CreateOrder/OrderStrip';
import { BLANK_ITEM, calcItemTotal } from './CreateOrder/itemPricing';

import { Screen } from '@/components/data';

/**
 * Booking an order is one page and one pass down it.
 *
 * Buyer at the top, items in a grid, the order's own facts and its money down
 * the right. Nothing opens on top of it: the product type, the process list and
 * the spec sheet used to be captured here, in a full-screen dialog per item,
 * and none of them are answerable while the customer is still on the phone.
 * They are answered on the job card, one line item at a time.
 */

export default function CreateOrderPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  // Customer selection
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerErrors, setNewCustomerErrors] = useState({});
  const [newCustomer, setNewCustomer] = useState({
    display_name: '',
    mobile_number: '',
    email: '',
    delivery_address: '',
    address: '',
  });
  const [jobType, setJobType] = useState('new');
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Repeat order data
  const [pastOrders, setPastOrders] = useState([]);
  const [pastItems, setPastItems] = useState([]);
  const [repeatTab, setRepeatTab] = useState('orders');
  const [pastOrdersOpen, setPastOrdersOpen] = useState(true);

  // Order details
  const [orderDetails, setOrderDetails] = useState({
    priority: 'medium',
    po_ref_number: '',
    po_date: '',
    order_source: '',
    order_value: '',
    discount_pct: '',
    gst_pct: '',
    advance_payment: '',
    payment_mode: '',
    remarks: '',
  });
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [stakeholderTags, setStakeholderTags] = useState([]);

  const [lineItems, setLineItems] = useState([]);
  // The blank last row keeps this key when it becomes a line item, so React
  // reuses the same row rather than remounting it — otherwise the input you are
  // typing into loses focus on its first keystroke.
  const [draftSeq, setDraftSeq] = useState(0);
  const draftId = `draft-${draftSeq}`;

  // ── Data fetching ──
  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers', { params: { search: customerSearch, limit: 50 } });
      setCustomers(res.data.customers);
    } catch {}
  };

  useEffect(() => { fetchCustomers(); }, [customerSearch]);

  useEffect(() => {
    if (selectedCustomer && jobType === 'repeat') {
      Promise.all([
        api.get(`/customers/${selectedCustomer.id}/past-orders`),
        api.get(`/customers/${selectedCustomer.id}/past-items`),
      ]).then(([ordersRes, itemsRes]) => {
        setPastOrders(ordersRes.data.orders || []);
        setPastItems(itemsRes.data.items || []);
      }).catch(() => { setPastOrders([]); setPastItems([]); });
    }
  }, [selectedCustomer, jobType]);

  // ── Item handlers ──
  const updateItem = (id, patch) => {
    setLineItems(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)));
  };

  // typing anywhere in the always-blank last row turns it into a line item
  // a row starts at one piece: most signage is one board, and a count typed
  // over it is a keystroke — a row left at nothing reaches the loading bay as
  // goods nobody can count
  const addDraft = (patch) => {
    setLineItems(prev => [...prev, { ...BLANK_ITEM, quantity: '1', ...patch, id: draftId }]);
    setDraftSeq(n => n + 1);
  };

  const removeItem = (id) => setLineItems(prev => prev.filter(i => i.id !== id));

  const repeatItem = (item, idx = 0) => ({
    ...BLANK_ITEM,
    id: `repeat-${Date.now()}-${idx}`,
    item_name: item.item_name || item.name || item.product_subtype || '',
    quantity: String(item.quantity ?? ''),
    price_type: item.price_type || 'per_piece',
    item_value: item.item_value || '',
    price_per_piece: item.price_per_piece || '',
    price_per_sheet: item.price_per_sheet || '',
    pieces_per_sheet: item.pieces_per_sheet || '',
    special_instructions: item.special_instructions || '',
    catalog_item_id: item.catalog_item_id || null,
    // a repeat runs the same way as the original, so its job card starts from
    // the same product type and specification rather than from nothing
    order_type: item.order_type || null,
    source_order_id: item.source_order_id || null,
    source_line_item_id: item.id || item.source_line_item_id || null,
  });

  const cloneOrder = (order) => {
    const cloned = order.line_items.map((item, idx) => ({
      ...repeatItem(item, idx),
      source_order_id: order.id,
      source_line_item_id: item.id,
    }));
    setLineItems(prev => [...prev, ...cloned]);
    toast.success(`Added ${cloned.length} items from ${order.order_id}`);
  };

  const cloneItem = (item) => {
    setLineItems(prev => [...prev, repeatItem(item)]);
    toast.success('Item added to order');
  };

  // ── Calculated values ──
  const lineItemsTotal = lineItems.reduce((sum, item) => sum + calcItemTotal(item), 0);

  // discount then GST, the order the strip shows them in
  const orderTotal = (() => {
    const taxable = lineItemsTotal - lineItemsTotal * (parseFloat(orderDetails.discount_pct) || 0) / 100;
    return taxable + taxable * (parseFloat(orderDetails.gst_pct) || 0) / 100;
  })();

  // ── Submit ──

  const submitOrder = async () => {
    if (!selectedCustomer) { toast.error('Pick a customer first'); return; }
    const named = lineItems.filter(item => item.item_name?.trim());
    if (named.length === 0) { toast.error('Add at least one item'); return; }
    const uncounted = named.find(item => (parseInt(item.quantity) || 0) < 1);
    if (uncounted) {
      toast.error(`How many ${uncounted.item_name.trim()}? Type the quantity — three boards is 3.`);
      return;
    }

    setLoading(true);
    try {
      // a name typed for the first time joins the catalogue when the order is
      // placed — the server does it, so it lands with the customer and the
      // product type the desk booked it under
      const items = named;
      const payload = {
        customer_id: selectedCustomer.id,
        expected_delivery_date: expectedDeliveryDate ? new Date(expectedDeliveryDate).toISOString() : '',
        priority: orderDetails.priority,
        job_type: jobType,
        order_source: orderDetails.order_source || null,
        po_ref_number: orderDetails.po_ref_number,
        po_date: orderDetails.po_date ? new Date(orderDetails.po_date).toISOString() : '',
        order_value: orderTotal,
        discount_pct: parseFloat(orderDetails.discount_pct) || 0,
        gst_pct: parseFloat(orderDetails.gst_pct) || 0,
        delivery_address: deliveryAddress || null,
        advance_payment: parseFloat(orderDetails.advance_payment) || 0,
        payment_mode: orderDetails.payment_mode,
        remarks: orderDetails.remarks,
        stakeholder_tags: stakeholderTags.map(tag => ({ user_id: tag.user_id, role_note: tag.role_note || '' })),
        line_items: items.map(item => ({
          // no order_type: which product type this is, and therefore how it is
          // made, is answered on the job card
          ...(item.order_type ? { order_type: item.order_type } : {}),
          item_name: item.item_name.trim(),
          quantity: parseInt(item.quantity) || 0,
          special_instructions: item.special_instructions || '',
          item_value: calcItemTotal(item),
          price_per_piece: parseFloat(item.price_per_piece) || 0,
          price_per_sheet: parseFloat(item.price_per_sheet) || 0,
          pieces_per_sheet: parseInt(item.pieces_per_sheet) || 1,
          price_type: item.price_type,
          expected_delivery_date: item.expected_delivery_date || null,
          needs_design_changes: jobType !== 'repeat',
          source_order_id: item.source_order_id || null,
          source_line_item_id: item.source_line_item_id || null,
          catalog_item_id: item.catalog_item_id || null,
          catalog_price_source: item.catalog_price_source || null,
          // the run is longer than the order when the desk says so; the extra
          // goes on the shelf when the job finishes
          surplus_quantity: parseInt(item.surplus_quantity, 10) || 0,
          fulfill_from_stock: !!item.fulfill_from_stock,
        })),
      };
      const res = await api.post('/orders', payload);
      toast.success(`Order ${res.data.order_id} created — fill each job card to start production`);
      navigate(`/orders/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to create order'));
    }
    setLoading(false);
  };

  const createCustomerInline = async () => {
    const errors = {};
    const nameErr = validateField('display_name', newCustomer.display_name, [{ name: 'required', options: { message: 'Firm name is required' } }]);
    if (nameErr) errors.display_name = nameErr;
    const gstinErr = validateField('gstin', newCustomer.gstin, [{ name: 'gstin' }]);
    if (gstinErr) errors.gstin = gstinErr;
    const mobileErr = validateField('mobile_number', newCustomer.mobile_number, [{ name: 'mobile', options: { allowEmpty: true } }]);
    if (mobileErr) errors.mobile_number = mobileErr;
    const emailErr = validateField('email', newCustomer.email, [{ name: 'email' }]);
    if (emailErr) errors.email = emailErr;
    setNewCustomerErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors in the customer form');
      return;
    }
    try {
      const res = await api.post('/customers', newCustomer);
      setSelectedCustomer(res.data);
      setShowNewCustomer(false);
      setNewCustomerErrors({});
      toast.success('Customer created');
      fetchCustomers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : (detail || 'Failed to create customer'));
    }
  };

  const namedCount = lineItems.filter(i => i.item_name?.trim()).length;

  return (
    <Screen data-testid="create-order-page">
      {/* One header bar: what this is, and the one button that finishes it. */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-xl font-bold text-slate-900">Create Order</h1>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            {namedCount} item{namedCount !== 1 ? 's' : ''}
            {lineItemsTotal > 0 && ` · ₹${lineItemsTotal.toLocaleString()}`}
          </span>
          <Button onClick={submitOrder} disabled={loading || namedCount === 0 || !selectedCustomer}
            className="bg-green-600 hover:bg-green-700 text-white px-6" data-testid="create-order-btn">
            {loading ? 'Creating...' : 'Create Order'}
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <BuyerPanel
            jobType={jobType}
            setJobType={setJobType}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            showNewCustomer={showNewCustomer}
            setShowNewCustomer={setShowNewCustomer}
            newCustomer={newCustomer}
            setNewCustomer={setNewCustomer}
            newCustomerErrors={newCustomerErrors}
            customers={customers}
            createCustomerInline={createCustomerInline}
            deliveryAddress={deliveryAddress}
            setDeliveryAddress={setDeliveryAddress}
          />

          <ItemsGrid
            lineItems={lineItems}
            calcItemTotal={calcItemTotal}
            onChange={updateItem}
            onAddDraft={addDraft}
            onRemove={removeItem}
            draftId={draftId}
            customerId={selectedCustomer?.id}
            stockPlanning
          />

          <section className="border border-slate-200 rounded">
            <header className="bg-brand-blue px-3 py-1.5 rounded-t">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white">Narration</h3>
            </header>
            <Textarea
              value={orderDetails.remarks}
              onChange={e => setOrderDetails(d => ({ ...d, remarks: e.target.value }))}
              placeholder="Anything about this order as a whole — terms, packing notes, who to call on delivery."
              rows={3}
              className="text-sm border-0 rounded-t-none shadow-none focus-visible:ring-1 resize-none"
            />
          </section>

          {/* Repeat order past items/orders */}
          {jobType === 'repeat' && selectedCustomer && (
            <Collapsible open={pastOrdersOpen} onOpenChange={setPastOrdersOpen}>
              <Card className="border-slate-200 shadow-sm">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 flex flex-row items-center justify-between py-3">
                    <CardTitle className="font-heading text-sm flex items-center gap-2">
                      <History className="h-4 w-4 text-slate-500" /> Past Orders
                    </CardTitle>
                    {pastOrdersOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <Tabs value={repeatTab} onValueChange={setRepeatTab}>
                      <TabsList className="mb-3">
                        <TabsTrigger value="orders">By Order</TabsTrigger>
                        <TabsTrigger value="items">By Item</TabsTrigger>
                      </TabsList>
                      <TabsContent value="orders">
                        {pastOrders.length === 0 ? (
                          <p className="text-sm text-slate-400 text-center py-4">No past orders found</p>
                        ) : pastOrders.slice(0, 5).map(order => (
                          <div key={order.id} className="p-3 border rounded-lg mb-2 hover:bg-slate-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium">{order.order_id}</p>
                                <p className="text-xs text-slate-500">{order.line_items?.length || 0} items • {formatIST(order.created_at, { dateOnly: true })}</p>
                              </div>
                              <Button size="sm" variant="outline" onClick={() => cloneOrder(order)}>
                                <RefreshCw className="h-3 w-3 mr-1" /> Repeat
                              </Button>
                            </div>
                          </div>
                        ))}
                      </TabsContent>
                      <TabsContent value="items">
                        {pastItems.length === 0 ? (
                          <p className="text-sm text-slate-400 text-center py-4">No past items found</p>
                        ) : pastItems.slice(0, 10).map(item => (
                          <div key={item.source_line_item_id} className="p-2 border rounded-lg mb-2 flex items-center justify-between hover:bg-slate-50">
                            <div>
                              <p className="text-xs font-medium">{item.item_name || item.product_subtype}</p>
                              <p className="text-[10px] text-slate-400">{item.order_type} • Qty: {item.quantity}</p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => cloneItem(item)}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </div>

        <OrderStrip
          orderDetails={orderDetails}
          setOrderDetails={setOrderDetails}
          expectedDeliveryDate={expectedDeliveryDate}
          setExpectedDeliveryDate={setExpectedDeliveryDate}
          stakeholderTags={stakeholderTags}
          setStakeholderTags={setStakeholderTags}
          itemsTotal={lineItemsTotal}
          entryDate={new Date().toLocaleDateString('en-IN')}
        />
      </div>
    </Screen>
  );
}
